import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import type { DataSelect, StatusObject } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type { OnChangeProp, OnChangePropValue } from 'webapp/models-custom/metric-setup-base';
import MetricSetupForClient from 'webapp/models-custom/metric-setup-for-client';
import type ClientMetricRecord from 'webapp/models/client-metric';
import type SampleRecord from 'webapp/models/sample';
import type { SampleData } from 'webapp/models/sample';

export default class ClientMetric extends BufferedModel<
	ClientMetricRecord,
	RecordData.ClientMetric.Serialized
> {
	@service('model-manager/client-metrics')
	declare mmClientMetricsService: Service.ModelManager.ClientMetrics;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/blocks')
	declare mmBlocksService: Service.ModelManager.Blocks;
	@service('timer') declare timerService: Service.Timer;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	// ------- props from / to ED record ------

	get id(): string {
		return this.model.id!;
	}

	get spaceId(): number {
		return this.getBuffered('spaceId');
	}

	set spaceId(value: number) {
		this.setBuffered('spaceId', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get blockId(): number {
		return this.model.blockId;
	}

	get dateRange(): DateRangeInterface.FormulaInterface {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface) {
		this.setBuffered('dateRange', value);
	}

	get metricSetupSpaceAccessId(): number | null {
		return this.getBuffered('metricSetupSpaceAccessId');
	}

	set metricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('metricSetupSpaceAccessId', value);
	}

	get metricSetupKey(): string {
		return this.getBuffered('metricSetupKey');
	}

	set metricSetupKey(value: string) {
		this.setBuffered('metricSetupKey', value);
	}

	get metricSetupDimensions(): string[] {
		return this.getBuffered('metricSetupDimensions');
	}

	set metricSetupDimensions(value: string[]) {
		this.setBuffered('metricSetupDimensions', value);
	}

	get metricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('metricSetupFilters');
	}

	set metricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('metricSetupFilters', value);
	}

	// ------- other props ------

	get isGeneric(): boolean {
		return !this.metricSetupSpaceAccessId;
	}

	// At this level, we don't have information about actual client metrics,
	// this is why we have to split the key and rely on metric key structure
	get sourceTypeKey(): string | null {
		return this.isGeneric ? this.metricSetupKey.split('@')[0]! : null;
	}

	get attributeName() {
		return this.metricSetup.firstAttributeNameFromBasicFilters;
	}

	get unitName(): string | null {
		return this.metricSetup.unitName;
	}

	private _metricSetup: MetricSetupForClient | null = null;

	@cached
	get metricSetup(): MetricSetupForClient {
		const currentSetupMetric = this._metricSetup?.metric;

		const newSetup = new MetricSetupForClient(
			{
				key: this.metricSetupKey,
				spaceAccessId: this.metricSetupSpaceAccessId,
				dimensions: this.metricSetupDimensions,
				filters: this.metricSetupFilters,
			},
			this.handleMetricSetupUpdate.bind(this),
		);

		if (
			newSetup.key === currentSetupMetric?.key &&
			newSetup.spaceAccessId === currentSetupMetric?.spaceAccessId
		) {
			newSetup.setMetric(currentSetupMetric);
		}

		this._metricSetup = newSetup;

		return this._metricSetup;
	}

	get sample(): SampleRecord | null {
		return this.mmSamplesService.findRecord(this.blockId.toString()) ?? null;
	}

	get mustChooseSource(): boolean {
		const sourcesForClient =
			this.mmClientMetricsService.sourceCountPerClientPerType[String(this.spaceId)];
		let mustChoose = false;

		if (this.sourceTypeKey) {
			const sourceCount = sourcesForClient?.[this.sourceTypeKey];
			if (sourceCount) {
				mustChoose = sourceCount > 1;
			}
		}

		return mustChoose;
	}

	@cached
	get sourceTypeName(): string | null {
		const sourceTypeKey = this.sourceTypeKey;
		let sourceName;

		if (sourceTypeKey) {
			sourceName = this.mmSourceTypesService.findModelById(+sourceTypeKey)?.name;
		}

		return sourceName || null;
	}

	get sampleData(): SampleData | null {
		return this.sample?.sampledata ?? null;
	}

	@cached
	get dataSelect(): DataSelect | null {
		const dataSelectRecord = this.mmDataSelectsService.getRecordsForBlock(this.blockId);
		const sampleData = this.sampleData;

		if (!sampleData || !dataSelectRecord) {
			return null;
		}

		const dsRecord = dataSelectRecord[0]!;

		const dataSelect = this.mmDataSelectsService.createModel(dsRecord);
		dataSelect.setFakeData(sampleData.dsData[0]!);

		return dataSelect;
	}

	get metricName(): string {
		return this.dataSelect?.dataRoot?.metricName ?? '';
	}

	get sourceInfo(): string {
		const { name, title } = this.dataSelect?.dataRoot?.source ?? {
			name: '',
			title: '',
		};

		return `${title} / ${name}`;
	}

	get statusObjects(): StatusObject[] {
		return this.dataSelect?.statusObjects ?? [];
	}

	get isNumberSyncing(): boolean {
		return this.statusObjects.some((so) => so.isSync);
	}

	private handleMetricSetupUpdate(prop: OnChangeProp, value: OnChangePropValue): void {
		switch (prop) {
			case 'key':
				this.metricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.metricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.metricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.metricSetupFilters = value as DSSetup.MetricSetupFiltersData;
				break;
		}
	}

	static override modelName = ModelName.CLIENT_METRIC; // store record type
}
